import React, { useMemo, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { LocalizedText as Text, useExhibitLanguage } from "../i18n/ExhibitLocaleContext";
import { formatExhibitText, tx } from "../i18n/exhibitLocale";

const SETS = [
  {
    stem: "The astronaut stepped onto the",
    options: [["moon", 71], ["surface", 14], ["station", 9], ["sandwich", 1]],
    second: {
      moon: [[".", 28], ["and", 25], ["carefully", 16], ["where", 8]],
      surface: [["of", 43], [".", 18], ["carefully", 12], ["and", 10]],
      station: [[".", 31], ["platform", 21], ["and", 17], ["deck", 9]],
      sandwich: [[".", 34], ["and", 19], ["by", 10], ["before", 8]],
    },
  },
  {
    stem: "For breakfast I had a bowl of",
    options: [["cereal", 58], ["oatmeal", 22], ["soup", 6], ["nails", 1]],
    second: {
      cereal: [[".", 34], ["and", 26], ["with", 21], ["while", 7]],
      oatmeal: [["with", 38], [".", 27], ["and", 19], ["that", 6]],
      soup: [[".", 41], ["and", 22], ["with", 15], ["for", 8]],
      nails: [[".", 44], ["and", 21], ["for", 9], ["because", 6]],
    },
  },
  {
    stem: "The dog chased the",
    options: [["ball", 44], ["cat", 31], ["mailman", 12], ["cloud", 2]],
    second: {
      ball: [["across", 30], ["into", 24], [".", 22], ["and", 12]],
      cat: [["up", 33], ["around", 25], [".", 20], ["out", 10]],
      mailman: [["down", 36], [".", 27], ["around", 18], ["all", 9]],
      cloud: [[".", 39], ["across", 22], ["and", 17], ["until", 8]],
    },
  },
];

function t(value, tier) {
  if (typeof value === "string") return value;
  if (tier === "el") return value.el ?? value.ms ?? value.hs;
  if (tier === "ms") return value.ms ?? value.hs ?? value.el;
  return value.hs ?? value.ms ?? value.el;
}

function displayNextWordStem(language, englishStem) {
  if (language !== "es") return englishStem;

  const base = SETS.find((item) => englishStem === item.stem || englishStem.startsWith(`${item.stem} `) || englishStem.startsWith(`${item.stem}.`));
  if (!base) return tx(language, englishStem);

  const translatedBase = tx(language, base.stem);
  const rest = englishStem.slice(base.stem.length);
  if (!rest) return translatedBase;

  const translatedRest = rest.replace(/[A-Za-z]+/g, (word) => tx(language, word));
  return `${translatedBase}${translatedRest}`;
}

export default function NextWordScreen({ tier = "hs", onBack = () => {}, onNarrationMoment = () => {} }) {
  const language = useExhibitLanguage();
  const [setIndex, setSetIndex] = useState(0);
  const [step, setStep] = useState(0);
  const [stem, setStem] = useState(SETS[0].stem);
  const [options, setOptions] = useState(SETS[0].options);
  const [votes, setVotes] = useState({});
  const [revealed, setRevealed] = useState(false);
  const set = SETS[setIndex];
  const totalVotes = Object.values(votes).reduce((sum, n) => sum + n, 0);
  const visitorPick = useMemo(() => {
    const entries = Object.entries(votes);
    if (!entries.length) return null;
    return [...entries].sort((a, b) => b[1] - a[1])[0][0];
  }, [votes]);
  const machinePick = options[0]?.[0] ?? null;

  const loadSet = (index) => {
    const next = ((index % SETS.length) + SETS.length) % SETS.length;
    setSetIndex(next);
    setStep(0);
    setStem(SETS[next].stem);
    setOptions(SETS[next].options);
    setVotes({});
    setRevealed(false);
  };

  if (step === 2) {
    return (
      <View style={styles.container}>
        <TopBar onBack={onBack} />
        <View style={styles.center}>
          <Text style={styles.title}>{t({ el: "It predicts what text comes next. That is not the same as knowing what is true.", ms: "It predicts what text comes next. Prediction is not understanding.", hs: "Language models predict what text comes next. Prediction is not the same as understanding or truth." }, tier)}</Text>
          <Text style={styles.body}>{t({
            el: "Real chatbots predict small pieces of text one at a time. This exhibit uses whole words so the idea is easy to see. A likely next piece is not automatically a true one.",
            ms: "Real language models predict tokens — whole words or pieces of words — one at a time. This exhibit uses whole words to make the idea visible. Likely is not the same as true.",
            hs: "Real language models generate tokens — which can be whole words or pieces of words — one at a time. This exhibit simplifies that into whole-word choices. High probability is not the same as truth.",
          }, tier)}</Text>
          <View style={styles.sentencePill}><Text style={styles.sentenceText}>{displayNextWordStem(language, stem)}</Text></View>
          <View style={styles.actions}>
            <ActionButton text="Try another sentence →" onPress={() => loadSet(setIndex + 1)} />
            <QuietButton text="Back to the menu →" onPress={onBack} />
          </View>
        </View>
      </View>
    );
  }

  const vote = (word) => {
    if (revealed) return;
    setVotes((prev) => ({ ...prev, [word]: (prev[word] || 0) + 1 }));
  };

  const continueSentence = () => {
    if (!visitorPick) return;
    if (step === 0) {
      setStem(`${stem} ${visitorPick}`);
      setOptions(set.second[visitorPick]);
      setVotes({});
      setRevealed(false);
      setStep(1);
    } else {
      setStem(visitorPick === "." ? `${stem}.` : `${stem} ${visitorPick}`);
      onNarrationMoment("v2_next_word_takeaway");
      setStep(2);
    }
  };

  return (
    <View style={styles.container}>
      <TopBar onBack={onBack} />
      <View style={styles.center}>
        <Text style={styles.title}>What word comes next?</Text>
        <Text style={styles.eyebrow}>{formatExhibitText(language,"SENTENCE {current} OF {total}",{current:setIndex+1,total:SETS.length})}</Text>
        <Text style={styles.subtitle}>{revealed ? "Sample prediction scores, ranked like next-token choices." : "Everyone taps a guess. Then compare it with a sample prediction ranking."}</Text>
        <View style={styles.sentencePill}><Text style={styles.sentenceText}>{displayNextWordStem(language, stem)} <Text style={styles.accent}>____</Text></Text></View>
        <View style={styles.grid}>
          {options.map(([word, probability]) => {
            const count = votes[word] || 0;
            const top = revealed && word === machinePick;
            const yours = revealed && word === visitorPick;
            return (
              <Pressable key={word} onPress={() => vote(word)} style={({ pressed }) => [styles.card, top && styles.cardHot, yours && !top && styles.cardYours, pressed && styles.pressed]}>
                <View style={styles.cardRow}>
                  <Text style={styles.word}>{word === "." ? ". (end the sentence)" : word}</Text>
                  {revealed ? <Text style={styles.percent}>{probability}%</Text> : <Text style={styles.hint}>{count ? `${count} guessed this` : "tap to guess"}</Text>}
                </View>
                {revealed && <View style={styles.track}><View style={[styles.fill, { width: `${probability}%` }]} /></View>}
                {revealed && (top || yours) && <Text style={styles.badge}>{top && yours ? "YOU BOTH PICKED THIS" : top ? "SAMPLE MODEL'S TOP PICK" : "YOUR GUESS"}</Text>}
              </Pressable>
            );
          })}
        </View>
        {!revealed ? (
          <View style={styles.actions}>
            <ActionButton disabled={!totalVotes} text="Reveal the sample model guesses →" onPress={() => totalVotes && setRevealed(true)} />
            {step === 0 && <QuietButton text="try a different sentence" onPress={() => loadSet(setIndex + 1)} />}
          </View>
        ) : (
          <>
            <View style={styles.calls}>
              <Call label="YOU SAID" value={visitorPick} color="#5ADC9B" />
              <Call label="THE SAMPLE MODEL'S TOP PICK" value={machinePick} color="#4FD1FF" />
            </View>
            <Text style={styles.explain}>{visitorPick === machinePick ? "Same pick — you and the sample model both chose what seemed likely next." : "Different picks. The sample model is only estimating what text is likely to come next."}</Text>
            <ActionButton text={step === 0 ? `Add “${visitorPick}” and keep going →` : "So what does this mean? →"} onPress={continueSentence} />
          </>
        )}

      </View>
    </View>
  );
}

function TopBar({ onBack }) { return <View style={styles.topBar}><Pressable onPress={onBack}><Text style={styles.topLink}>← Menu</Text></Pressable><Text style={styles.topTitle}>Next-Word Line-Up</Text></View>; }
function ActionButton({ text, onPress, disabled = false }) { return <Pressable onPress={onPress} disabled={disabled} style={[styles.button, disabled && styles.disabled]}><Text style={styles.buttonText}>{text}</Text></Pressable>; }
function QuietButton({ text, onPress }) { return <Pressable onPress={onPress} style={styles.quiet}><Text style={styles.quietText}>{text}</Text></Pressable>; }
function Call({ label, value, color }) { return <View style={styles.call}><Text style={styles.callLabel}>{label}</Text><Text style={[styles.callValue, { color }]}>“{value}”</Text></View>; }

const styles = StyleSheet.create({
  container: { width: 1920, height: 1080, backgroundColor: "#070D17" },
  topBar: { height: 72, paddingHorizontal: 48, flexDirection: "row", alignItems: "center", gap: 26 },
  topLink: { color: "#8FA5BC", fontSize: 19, fontWeight: "800" },
  topTitle: { color: "#8FA5BC", fontSize: 19, fontWeight: "800" },
  center: { alignItems: "center", paddingHorizontal: 110 },
  title: { marginTop: 34, color: "#F4F8FC", fontSize: 60, fontWeight: "900" },
  eyebrow: { marginTop: 12, color: "#4FD1FF", fontSize: 17, fontWeight: "900", letterSpacing: 2.4 },
  subtitle: { marginTop: 10, color: "#9FB2C6", fontSize: 24 },
  sentencePill: { marginTop: 28, backgroundColor: "#0D1626", borderWidth: 1, borderColor: "#1B2A44", borderRadius: 28, paddingHorizontal: 44, paddingVertical: 22 },
  sentenceText: { color: "#F4F8FC", fontSize: 40, fontWeight: "900" }, accent: { color: "#4FD1FF" },
  grid: { marginTop: 30, width: 1310, flexDirection: "row", flexWrap: "wrap", gap: 18 },
  card: { width: 646, minHeight: 126, backgroundColor: "#0D1626", borderWidth: 1, borderColor: "#1B2A44", borderRadius: 20, paddingHorizontal: 28, paddingVertical: 20 },
  cardHot: { borderColor: "#4FD1FF", borderWidth: 2 }, cardYours: { borderColor: "#5ADC9B", borderWidth: 2 },
  cardRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 20 },
  word: { color: "#F4F8FC", fontSize: 30, fontWeight: "900" }, percent: { color: "#4FD1FF", fontSize: 30, fontWeight: "900" }, hint: { color: "#8FA5BC", fontSize: 17 },
  track: { marginTop: 14, height: 14, borderRadius: 10, overflow: "hidden", backgroundColor: "#132037" }, fill: { height: "100%", backgroundColor: "#4FD1FF", borderRadius: 10 },
  badge: { marginTop: 10, color: "#5ADC9B", fontSize: 14, fontWeight: "900", letterSpacing: 1.2 },
  actions: { marginTop: 26, flexDirection: "row", gap: 16, alignItems: "center" },
  button: { backgroundColor: "#0E3A50", borderWidth: 1, borderColor: "#4FD1FF", borderRadius: 14, paddingHorizontal: 28, paddingVertical: 16 }, buttonText: { color: "#F4F8FC", fontSize: 21, fontWeight: "900" }, disabled: { opacity: 0.35 },
  quiet: { paddingHorizontal: 22, paddingVertical: 14 }, quietText: { color: "#8FA5BC", fontSize: 19, fontWeight: "700" },
  calls: { marginTop: 24, flexDirection: "row", gap: 70 }, call: { minWidth: 320 }, callLabel: { color: "#8FA5BC", fontSize: 17, fontWeight: "800", letterSpacing: 1.8 }, callValue: { marginTop: 4, fontSize: 46, fontWeight: "900" },
  explain: { marginTop: 10, color: "#B9C8D8", fontSize: 23 }, tiny: { marginTop: 18, color: "#5F7690", fontSize: 16 },
  body: { marginTop: 28, color: "#B9C8D8", fontSize: 28, lineHeight: 42, maxWidth: 1150, textAlign: "center" }, pressed: { opacity: 0.9 },
});
